import { Ban, CheckCircle2, Loader2, Plus, Printer, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Ticket } from "@/components/Ticket.tsx";
import { getAutoPrint, markPrinted, setAutoPrint } from "@/lib/printing.ts";
import { IS_ONLINE_EDITION } from "@/lib/edition.ts";
import { VoidSaleDialog } from "@/components/VoidSaleDialog.tsx";
import { isAwaitingSunat, usePrintSale, useSale } from "@/queries/sales.ts";
import { useBranchTerminals } from "@/queries/terminals.ts";
import { useTenant } from "@/queries/tenant.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useTerminalStore } from "@/stores/terminal.ts";
import { formatPEN } from "@/lib/utils.ts";
import { SaleStatus, type SaleDetailOut } from "@/types/api.ts";

/** Cuánto se espera la respuesta de SUNAT antes de imprimir sin el QR. */
const SUNAT_WAIT_MS = 6_000;

export function SaleSuccessPage() {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const sale = useSale(saleId);
  const tenant = useTenant();
  const [voidOpen, setVoidOpen] = useState(false);
  const [autoPrint, setAutoPrintState] = useState(getAutoPrint);
  const [sunatWaitOver, setSunatWaitOver] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  // Impresión directa ESC/POS (opción B): si la caja tiene ticketera de red, el
  // server local imprime. Imprime la caja pareada a esta PWA o, si no hay, la
  // de la venta (el backend resuelve igual con X-Terminal-Id).
  const pairedTerminalId = useTerminalStore((s) => s.terminal?.id);
  const terminals = useBranchTerminals(IS_ONLINE_EDITION ? null : sale.data?.branch_id);
  const printTerminalId = pairedTerminalId ?? sale.data?.terminal_id;
  const printTerminal = terminals.data?.find((t) => t.id === printTerminalId);
  const directPrint = !IS_ONLINE_EDITION && Boolean(printTerminal?.printer_host);
  const terminalsResolved = IS_ONLINE_EDITION || terminals.isFetched;
  const { mutate: printDirect, isPending: printingDirect } = usePrintSale();

  /** Imprime por la ticketera de la caja; si falla, muestra el error y cae a
   *  window.print(). Solo la primera impresión abre el cajón. */
  const printTicket = (openDrawer: boolean) => {
    setPrintError(null);
    if (!directPrint || !saleId) {
      window.print();
      return;
    }
    printDirect(
      { sale_id: saleId, open_drawer: openDrawer },
      {
        onError: (err) => {
          setPrintError(err.message);
          requestAnimationFrame(() => window.print());
        },
      },
    );
  };

  // Impresión automática (con Chrome en modo kiosco sale sin diálogo). Si la
  // boleta espera a SUNAT, se dan unos segundos para que salga con el QR.
  const printable =
    sale.data?.status === SaleStatus.ISSUED &&
    Boolean(tenant.data) &&
    Boolean(saleId) &&
    terminalsResolved;
  const awaitingSunat = isAwaitingSunat(sale.data);
  useEffect(() => {
    if (!autoPrint || !printable || !awaitingSunat) return;
    const timer = setTimeout(() => setSunatWaitOver(true), SUNAT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [autoPrint, printable, awaitingSunat]);
  useEffect(() => {
    if (!autoPrint || !printable || (awaitingSunat && !sunatWaitOver)) return;
    if (!markPrinted(saleId!)) return;
    if (directPrint) {
      printDirect(
        { sale_id: saleId!, open_drawer: true },
        {
          onError: (err) => {
            setPrintError(err.message);
            requestAnimationFrame(() => window.print());
          },
        },
      );
      return;
    }
    // Un frame para que el ticket termine de pintarse antes de imprimir.
    const frame = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(frame);
  }, [autoPrint, printable, awaitingSunat, sunatWaitOver, saleId, directPrint, printDirect]);

  if (sale.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        Cargando comprobante…
      </div>
    );
  }
  if (sale.isError || !sale.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-red-600">No se pudo cargar la venta {saleId}.</p>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white"
        >
          Volver al POS
        </button>
      </div>
    );
  }

  const data = sale.data;
  const docNumber =
    data.series_code && data.document_number
      ? `${data.series_code}-${String(data.document_number).padStart(8, "0")}`
      : "—";

  const isVoided = data.status === SaleStatus.VOIDED;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="flex-1 px-6 py-10 print:hidden">
        <div className="mx-auto max-w-2xl space-y-6">
          <div className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-slate-200 bg-white p-8">
            <div
              className={
                isVoided
                  ? "rounded-full bg-red-100 p-3 text-red-700"
                  : "rounded-full bg-brand-50 p-3 text-brand-700"
              }
            >
              {isVoided ? <Ban className="h-10 w-10" /> : <CheckCircle2 className="h-10 w-10" />}
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
              {isVoided ? "Venta anulada" : "Venta emitida"}
            </h1>
            <p className="text-slate-500">
              Comprobante <span className="font-semibold text-slate-800">{docNumber}</span>
            </p>
            <p
              className={
                "text-3xl font-bold tabular-nums " +
                (isVoided ? "text-slate-400 line-through" : "text-slate-900")
              }
            >
              {formatPEN(data.total)}
            </p>
            {!isVoided && data.payments.some((p) => Number(p.change_given) > 0) && (
              <p className="text-sm text-emerald-700">
                Vuelto:{" "}
                {formatPEN(data.payments.reduce((acc, p) => acc + Number(p.change_given), 0))}
              </p>
            )}
            {isVoided && data.void_reason && (
              <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800">
                Motivo: {data.void_reason}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => printTicket(markPrinted(data.id))}
              disabled={printingDirect}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-4 text-base font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
            >
              {printingDirect ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Printer className="h-5 w-5" />
              )}{" "}
              Imprimir ticket
            </button>
            {printError && (
              <p className="w-full rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 sm:order-last">
                {printError} — se abrió la impresión del navegador.
              </p>
            )}
            <label className="flex w-full cursor-pointer items-center gap-2 text-sm text-slate-600 sm:order-last">
              <input
                type="checkbox"
                checked={autoPrint}
                onChange={(e) => {
                  setAutoPrint(e.target.checked);
                  setAutoPrintState(e.target.checked);
                }}
                className="h-4 w-4"
              />
              Imprimir automáticamente al emitir en esta caja
              <span className="text-xs text-slate-400">
                {directPrint
                  ? `(ticketera ${printTerminal?.printer_host})`
                  : "(sin diálogo si Chrome está en modo kiosco)"}
              </span>
            </label>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-brand-600 py-4 text-base font-extrabold text-white hover:bg-brand-700"
            >
              <Plus className="h-5 w-5" /> Nueva venta
            </button>
            {!isVoided && (
              <button
                type="button"
                onClick={() => setVoidOpen(true)}
                className="flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-white px-5 py-4 text-base font-medium text-red-700 hover:bg-red-50"
                title="Anular esta venta y revertir inventario"
              >
                <Trash2 className="h-5 w-5" /> Anular
              </button>
            )}
          </div>

          {/* Preview del ticket. La regla `@media print` en index.css oculta todo
              menos #print-area, así que este mismo nodo es el que se imprime. */}
          <div className="rounded-[var(--radius-card)] border border-dashed border-slate-300 bg-white p-4">
            <p className="mb-2 text-center text-xs tracking-wide text-slate-500 uppercase print:hidden">
              {isVoided ? "Comprobante (ANULADO)" : "Vista previa del ticket"}
            </p>
            <SunatBadge sale={data} />
            <Ticket
              sale={data}
              cashierName={user?.full_name}
              company={
                tenant.data
                  ? {
                      legal_name: tenant.data.legal_name,
                      ruc: tenant.data.ruc,
                      address: tenant.data.fiscal_address ?? undefined,
                    }
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      {voidOpen && (
        <VoidSaleDialog saleId={data.id} saleLabel={docNumber} onClose={() => setVoidOpen(false)} />
      )}
    </div>
  );
}

/** Estado SUNAT sobre la vista previa (no se imprime). */
function SunatBadge({ sale }: { sale: SaleDetailOut }) {
  if (sale.document_type !== "boleta" && sale.document_type !== "factura") return null;
  const doc = sale.electronic_document;
  let tone = "bg-slate-100 text-slate-700";
  let label = "SUNAT: en cola de envío (se imprime el QR cuando responda)";
  if (doc?.status === "accepted" || doc?.status === "observed") {
    tone = "bg-emerald-100 text-emerald-800";
    label = doc.status === "observed" ? "SUNAT: aceptado con observaciones" : "SUNAT: aceptado";
    if (doc.void_status === "pending") label += " · baja en proceso";
    if (doc.void_status === "accepted") label += " · dado de baja";
  } else if (doc?.status === "rejected") {
    tone = "bg-red-100 text-red-800";
    label = `SUNAT: rechazado${doc.sunat_message ? ` — ${doc.sunat_message}` : ""}`;
  }
  return (
    <p
      className={`mx-auto mb-3 flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-medium print:hidden ${tone}`}
    >
      {isAwaitingSunat(sale) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {label}
    </p>
  );
}
